import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronLeft, ChevronRight, DollarSign, Users, Star, Trophy, AlertTriangle, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL =
  window.APP_CONFIG?.BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  window.location.origin;
const API = `${BACKEND_URL}/api`;

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Tech Performance dashboard.
 * Aggregates from three sources:
 *   - paid invoices (revenue, credited to every tech in completed_by_techs)
 *   - completed jobs (job count + screens)
 *   - review click-tracking (reviews sent per tech)
 * Scoped to a PST month.
 */
export default function TechPerformance() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const runBackfill = async () => {
    if (!window.confirm(
      "Copy tech attribution from every past review-request onto the linked invoice? " +
      "Already-tagged invoices are skipped (safe to run repeatedly)."
    )) return;
    setBackfilling(true);
    try {
      const token = getSessionToken();
      const { data: result } = await axios.post(
        `${API}/invoices/backfill-completed-by-from-reviews`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const skipped = (result.skipped_no_invoice || 0) + (result.skipped_already_tagged || 0);
      toast.success(
        `Backfill complete — ${result.updated} invoice${result.updated === 1 ? "" : "s"} tagged` +
        (skipped ? ` (${skipped} skipped)` : "")
      );
      await load();
    } catch {
      toast.error("Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.get(`${API}/reports/tech-performance`, {
        params: { month: `${year}-${String(month).padStart(2, "0")}` },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch {
      toast.error("Failed to load tech performance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [year, month]);

  const prev = () => {
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else {
      setMonth(month - 1);
    }
  };
  const next = () => {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else {
      setMonth(month + 1);
    }
  };

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const topTechRevenue = data?.techs?.[0]?.revenue || 1; // avoid div-by-zero
  const untagged = data?.untagged;

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate("/settings")}
          data-testid="back-to-settings"
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Settings</span>
        </button>

        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Tech Performance</h1>
              <p className="text-sm text-zinc-500">Revenue &amp; jobs completed by screen tech</p>
            </div>
          </div>
          <button
            onClick={runBackfill}
            disabled={backfilling}
            data-testid="backfill-btn-header"
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold disabled:opacity-60"
            title="Backdate tech attribution from review requests already sent"
          >
            {backfilling ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Backdating…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Backdate from Reviews
              </>
            )}
          </button>
        </div>

        {/* Month switcher */}
        <div
          data-testid="month-switcher"
          className="bg-white rounded-xl border shadow-sm p-3 flex items-center justify-between mb-6"
        >
          <button
            onClick={prev}
            data-testid="prev-month"
            className="h-10 w-10 rounded-lg border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="text-lg font-semibold text-zinc-900">
              {MONTHS[month - 1]} {year}
            </div>
            {isCurrentMonth && (
              <div className="text-[11px] text-emerald-600 font-medium">Current month</div>
            )}
          </div>
          <button
            onClick={next}
            data-testid="next-month"
            disabled={isCurrentMonth}
            className="h-10 w-10 rounded-lg border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-zinc-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Totals row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard
                icon={<DollarSign className="w-4 h-4" />}
                label="Revenue"
                value={fmt(data?.totals?.revenue)}
                tint="emerald"
              />
              <StatCard
                icon={<Users className="w-4 h-4" />}
                label="Completed Jobs"
                value={data?.totals?.completed_jobs ?? 0}
                tint="blue"
              />
              <StatCard
                icon={<Users className="w-4 h-4" />}
                label="Screens"
                value={data?.totals?.screens_completed ?? 0}
                tint="purple"
              />
              <StatCard
                icon={<Star className="w-4 h-4" />}
                label="Reviews Sent"
                value={data?.totals?.reviews_sent ?? 0}
                tint="amber"
              />
            </div>

            {/* Untagged warning */}
            {untagged && untagged.invoice_count > 0 && (
              <div
                data-testid="untagged-banner"
                className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 mb-6 flex items-start gap-3"
              >
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-amber-900">
                    {untagged.invoice_count} paid invoice{untagged.invoice_count === 1 ? "" : "s"}
                    {" "}not attributed ({fmt(untagged.revenue)})
                  </div>
                  <div className="text-amber-800 mt-0.5">
                    Backdate the tech from the review-request you already sent, or tag them by hand from the invoice page.
                  </div>
                  <button
                    onClick={runBackfill}
                    disabled={backfilling}
                    data-testid="backfill-btn"
                    className="mt-2 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm disabled:opacity-60"
                  >
                    {backfilling ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Backdating…
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Backdate from Reviews
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Tech leaderboard */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="tech-leaderboard">
              <div className="p-4 border-b border-zinc-100">
                <h2 className="font-semibold text-zinc-900">Leaderboard</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Revenue goes to the primary (first-listed) tech on each invoice. Secondary techs still get job &amp; screen credit.
                </p>
              </div>
              {(!data?.techs || data.techs.length === 0) ? (
                <div className="p-12 text-center">
                  <p className="text-zinc-500 text-sm">No tech-attributed activity yet this month.</p>
                  <p className="text-zinc-400 text-xs mt-1">Send a review request from any paid invoice to tag the tech.</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {data.techs.map((t, idx) => {
                    const pct = Math.max(4, Math.round((t.revenue / topTechRevenue) * 100));
                    return (
                      <div
                        key={t.tech_name}
                        data-testid={`tech-row-${t.tech_name}`}
                        className="p-4 hover:bg-zinc-50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              idx === 0 ? "bg-amber-100 text-amber-800" :
                              idx === 1 ? "bg-zinc-200 text-zinc-800" :
                              idx === 2 ? "bg-orange-100 text-orange-800" :
                              "bg-zinc-100 text-zinc-600"
                            }`}>
                              {idx + 1}
                            </div>
                            <div>
                              <div className="font-semibold text-zinc-900">{t.tech_name}</div>
                              <div className="text-xs text-zinc-500">
                                {t.completed_jobs} job{t.completed_jobs === 1 ? "" : "s"}
                                {" · "}
                                {t.screens_completed} screen{t.screens_completed === 1 ? "" : "s"}
                                {" · "}
                                {t.reviews_sent} review{t.reviews_sent === 1 ? "" : "s"}
                                {t.assist_count > 0 && (
                                  <>
                                    {" · "}
                                    <span className="text-zinc-400" title="Jobs where this tech was secondary — revenue credited to the primary tech.">
                                      {t.assist_count} assist{t.assist_count === 1 ? "" : "s"}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-emerald-700">{fmt(t.revenue)}</div>
                            <div className="text-[11px] text-zinc-500">
                              {t.invoice_count} paid invoice{t.invoice_count === 1 ? "" : "s"}
                            </div>
                          </div>
                        </div>
                        {/* Progress bar — relative to top tech */}
                        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, tint }) {
  const tints = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
  };
  return (
    <div className={`rounded-xl border p-3 ${tints[tint] || tints.emerald}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
